import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  saveSearchDetails,
  saveHotelDetails,
  setDetailTransaction,
} from '@shared/redux/actions';
import { getHotelsDetail } from '@shared/services/services';
import getQuantity from '../getQuantity';

export default function useHotelDetail(params) {
  const dispatch = useDispatch();
  const [hotelDetail, setHotelDetail] = useState(undefined);
  const [searchFilters, setSearchFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  // Ref para controlar que fetch se haga solo una vez por montaje
  const didFetchRef = useRef(false);

  const parseSearch = filters => ({
    checkIn: filters.check_in,
    checkOut: filters.check_out,
    passengers: filters.passengers,
    totalPassengers: filters.totalpassengers,
    totalNights: filters.totalnights,
    hotelCode: null,
    search_option: filters.search_option,
    city: {
      id: filters.city_selected?.id,
      label: filters.city_selected?.label,
      country: filters.country,
      countryOfResidence: filters.country_of_residence,
      name: filters.city_selected?.label?.split(',')[0]?.trim(),
    },
  });

  useEffect(() => {
    // Evitar doble fetch
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    const fetchHotel = async () => {
      setIsLoading(true);
      try {
        const result = await getHotelsDetail(
          {
            id_hotel: params.urlHotelId,
            hotel_code: params.urlHotelCode,
            id_integration: params.urlIntegrationId,
            id_city: params.urlIdCity,
            id_provider: params.urlIdProvider,
          },
          { token: params.urlToken }
        );

        if (!result?.rate?.length)
          throw new Error('No se encontraron resultados');

        // Guardar transaction en redux
        dispatch(setDetailTransaction(result.detail_transaction));

        // Parsear search filters
        const parsedSearch = parseSearch(result.search);
        setSearchFilters(parsedSearch);

        // Construir hotelDetail con rates
        const hotel = {
          ...result.detail,
          rate: [...(result.rate[0]?.rates || [])],
        };
        setHotelDetail(hotel);

        // Guardar detalle de hotel en redux
        dispatch(
          saveHotelDetails({
            idIntegration: params.urlIntegrationId,
            tokens: params.urlToken,
            idHotel: params.urlHotelId,
            hotelCode: params.urlHotelCode,
            idCity: params.urlIdCity,
            hotelDetails: hotel,
          })
        );

        // Guardar search details en redux
        const quantity = getQuantity(parsedSearch);
        dispatch(
          saveSearchDetails({
            passengers: parsedSearch.passengers,
            checkIn: parsedSearch.checkIn,
            checkOut: parsedSearch.checkOut,
            totalPassengers: parsedSearch.totalPassengers,
            totalNights: parsedSearch.totalNights,
            city: parsedSearch.city,
            search_option: parsedSearch.search_option,
            searchId: Date.now(),
            quantity,
            roomInfo: {
              total_passengers: parsedSearch.totalPassengers,
              totalRooms: quantity?.length || 0,
            },
          })
        );
      } catch (err) {
        console.error(err);
        setHasErrors(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotel();
  }, [
    params.urlHotelId,
    params.urlHotelCode,
    params.urlIntegrationId,
    params.urlIdCity,
    params.urlIdProvider,
    params.urlToken,
    dispatch,
  ]);

  return { hotelDetail, searchFilters, isLoading, hasErrors };
}
